import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionKicker, SectionHeading, FadeIn, Stat } from "@/components/shared/SectionBits";
import { ArrowUpRight, Target, Eye, Compass, Award } from "lucide-react";

const HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/kfum94cs_CP1.png";
const TEAM = "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/hk9ksgqq_leadership.png";

const TIMELINE = [
    { year: "1991", title: "Joined the British Armed Forces", desc: "Enlisted into the British Army, beginning a long career in service and security." },
    { year: "2005", title: "Transferred to the Royal Military Police", desc: "Moved from the Infantry to the Royal Military Police, developing investigative and protection expertise." },
    { year: "2005", title: "Civilian career in IT security", desc: "Started civilian employment with a national IT security company, bringing military discipline into the private sector." },
    { year: "2010", title: "Close Protection training", desc: "Completed formal Close Protection training, adding a hands-on physical protection capability." },
    { year: "2013", title: "SIA teaching qualifications", desc: "Attained teaching qualifications and started delivering SIA training to the next generation of professionals." },
    { year: "2019", title: "Surveillance course completed", desc: "Completed advanced surveillance training, deepening our investigative and TSCM capability." },
    { year: "2026", title: "UHNWI & Royal advisory", desc: "Continuing consulting for ultra-high-net-worth individuals and visiting foreign royalty." },
];

const VALUES = [
    { icon: Compass, title: "Integrity", desc: "Independent advice. No commissions. No hidden agenda." },
    { icon: Eye, title: "Discretion", desc: "Everything we do is designed to be quiet, professional and confidential." },
    { icon: Target, title: "Evidence", desc: "Recommendations grounded in method, data and defensible reasoning." },
    { icon: Award, title: "Excellence", desc: "Senior practitioners. Serious engagements. Precise execution." },
];

const LEADERS = []; // reserved for future team profiles

export default function AboutPage() {
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="relative min-h-[70vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO} alt="London Gherkin skyscraper" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-dr-navy/95 via-dr-navy/70 to-dr-navy/30" />
                </div>
                <div className="relative dr-container w-full py-24">
                    <SectionKicker>About Dynamic Risks</SectionKicker>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-4xl"
                    >
                        A quieter kind of risk advisory.
                    </motion.h1>
                    <p className="text-white/80 text-lg md:text-xl mt-7 max-w-2xl">
                        We exist because complex risk deserves better than uniformed reassurance and pattern-matched
                        checklists. Our clients deserve considered judgment, evidence, and calm execution.
                    </p>
                </div>
            </section>

            {/* Story / Mission / Vision */}
            <section className="dr-container py-28 md:py-36">
                <div className="grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <SectionKicker>Our story</SectionKicker>
                        <SectionHeading>Why Dynamic Risks exists.</SectionHeading>
                    </div>
                    <div className="lg:col-span-7 text-lg text-dr-dark/80 leading-relaxed space-y-6">
                        <p>
                            Dynamic Risks was established in response to a simple but important observation:
                            organisations are too often presented with security products and services before their
                            actual risks and requirements have been properly understood.
                        </p>
                        <p>
                            I founded the business to provide an alternative, independent, experience-led advice
                            that is informed by intelligence, grounded in operational reality and focused on
                            achieving the right outcome for the client rather than promoting a predetermined
                            solution.
                        </p>
                        <p>
                            Throughout my career, I have operated across complex and demanding environments where
                            risk is rarely straightforward and no two situations are the same. That experience has
                            reinforced a fundamental principle that continues to shape the way Dynamic Risks Group
                            operates today: effective security cannot be delivered through a standardised or
                            one-size-fits-all approach.
                        </p>
                        <p>
                            It requires a clear understanding of the operating environment, the ability to identify
                            and assess credible threats and vulnerabilities, and the experience to develop
                            solutions that are proportionate, practical and appropriate to the circumstances.
                        </p>
                        <p>
                            Today, Dynamic Risks Group supports businesses, legal professionals, private clients
                            and organisations across the UK, providing confidential, intelligence-led consultancy
                            across security, investigations, cyber risk and Technical Surveillance Countermeasures
                            (TSCM).
                        </p>
                        <p>Every engagement begins with understanding the client.</p>
                        <p>
                            Before making recommendations, we take the time to understand the organisation, its
                            people, assets, operating environment and objectives. We then identify the relevant
                            risks and vulnerabilities before determining the most appropriate course of action.
                        </p>
                        <p>
                            This approach ensures that recommendations are driven by evidence and genuine
                            operational requirements rather than by the need to sell a particular product or
                            service.
                        </p>
                        <p>
                            A further strength of Dynamic Risks Group is our ability to access specialist expertise
                            when required.
                        </p>
                        <p>
                            Modern security challenges are increasingly complex and often span multiple
                            disciplines. No single consultancy can credibly claim to possess every capability
                            internally. Over many years, I have developed a trusted network of carefully selected
                            specialists and partner organisations across a broad range of security, intelligence,
                            investigative, cyber and technical disciplines.
                        </p>
                        <p>
                            Where a client&apos;s requirements extend beyond our core expertise, we bring together
                            the appropriate specialists to create a coordinated solution. This allows Dynamic Risks
                            Group to remain independent while ensuring clients have access to the knowledge,
                            capability and experience their particular circumstances demand.
                        </p>
                        <p>Our role is therefore not simply to provide security services.</p>
                        <p>
                            It is to act as a trusted and independent risk adviser, providing clear insight,
                            coordinating specialist expertise where required, and enabling clients to make informed
                            decisions with confidence.
                        </p>
                        <p>
                            Whether supporting a corporate organisation seeking to strengthen its security posture,
                            a legal team requiring discreet investigative or intelligence support, or a private
                            client facing a complex security concern, our approach remains consistent:
                        </p>
                        <p className="font-display text-dr-navy text-xl font-semibold">
                            Understand the environment. Identify the risk. Provide clear advice. Deliver the right
                            solution.
                        </p>
                        <p>
                            Above all, our recommendations are based on what is right for the client, not on
                            commercial relationships, predetermined products or off-the-shelf solutions.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-6 pt-8">
                            <div className="border border-dr-silver rounded-xl p-6 bg-dr-grey">
                                <p className="text-xs uppercase tracking-[0.24em] text-dr-blue font-medium">Mission</p>
                                <p className="font-display font-semibold text-dr-navy text-lg mt-2">
                                    Helping you understand and manage risk through a no-nonsense consultative approach.
                                </p>
                            </div>
                            <div className="border border-dr-silver rounded-xl p-6 bg-dr-grey">
                                <p className="text-xs uppercase tracking-[0.24em] text-dr-blue font-medium">Vision</p>
                                <p className="font-display font-semibold text-dr-navy text-lg mt-2">
                                    To be among the UK&apos;s most trusted independent adviser&apos;s on integrated risk and protective security.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="bg-dr-grey py-28 md:py-36">
                <div className="dr-container">
                    <SectionKicker>Our values</SectionKicker>
                    <SectionHeading className="max-w-3xl">Four principles that guide every engagement.</SectionHeading>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
                        {VALUES.map((v, i) => (
                            <FadeIn key={v.title} delay={i * 0.08}>
                                <div className="bg-white border border-dr-silver rounded-2xl p-8 h-full">
                                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-dr-navy text-white">
                                        <v.icon className="w-5 h-5" />
                                    </span>
                                    <h3 className="font-display text-2xl font-bold text-dr-navy mt-6">{v.title}</h3>
                                    <p className="text-dr-dark/70 mt-3 leading-relaxed">{v.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline removed per client direction */}

            {/* Leadership */}
            <section className="bg-dr-navy py-28 md:py-36 dr-grain">
                <div className="dr-container">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-8">
                            <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium mb-4">Leadership</p>
                            <h2 className="font-display font-bold text-white text-4xl md:text-5xl leading-tight">
                                Over thirty years of operational, hands-on experience.
                            </h2>
                        </div>
                        <div className="md:col-span-4">
                            <img src={TEAM} alt="Team collaboration" className="rounded-2xl w-full aspect-square object-cover" />
                        </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-14">
                        <div className="max-w-3xl space-y-6 text-white/85 text-lg leading-relaxed">
                            <p>
                                My professional career has been shaped by over two decades of service in both the
                                British Army and Army Reserve, serving within the Infantry and the Royal Military
                                Police. During this time, I developed extensive experience leading teams in demanding
                                operational environments, delivering training, conducting investigations, and
                                supporting military operations across the UK and overseas. My service included
                                operational deployments and multinational exercises, where success depended upon
                                effective leadership, disciplined decision-making, and the ability to assess and
                                manage risk in fast-moving and often unpredictable situations.
                            </p>
                            <p>
                                Following my military career, I transitioned into private security, where I have
                                continued to build upon those foundations supporting high-net-worth individuals,
                                corporate executives and private clients. My approach combines operational
                                awareness with discretion, professionalism, and a strong understanding of the
                                legal and ethical frameworks that underpin modern protective security and
                                investigative practice.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Accreditations removed per client direction */}
            <section className="dr-container py-24">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-dr-silver pt-10">
                    <p className="font-display text-2xl text-dr-navy max-w-xl leading-snug">
                        Have a sensitive matter to discuss?
                    </p>
                    <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-dr-navy text-white font-semibold hover:bg-dr-blue transition-colors">
                        Speak in confidence <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
